import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Briefcase,
  FileText,
  Clock,
  Shield,
  Gavel,
  Users,
  AlertTriangle,
  Scale,
  Truck,
  Landmark,
  FileCheck,
  MessageCircle,
  CheckCircle2,
  Building2,
  ChevronDown,
  HelpCircle,
} from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de uma consulta sobre Direito do Trabalho.";

const TrabalhistaPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      pergunta: "Qual o prazo para entrar com uma ação trabalhista?",
      resposta: "O trabalhador tem até 2 anos após o término do contrato de trabalho para ajuizar uma ação trabalhista. Nessa ação, pode cobrar direitos dos últimos 5 anos trabalhados. Por isso, é importante não deixar para a última hora."
    },
    {
      pergunta: "Fui demitido sem justa causa. Quais são meus direitos?",
      resposta: "Você tem direito a: aviso prévio (trabalhado ou indenizado), saldo de salário, férias vencidas e proporcionais + 1/3, 13º salário proporcional, liberação do FGTS com multa de 40%, e seguro-desemprego (se cumprir os requisitos)."
    },
    {
      pergunta: "Trabalhei sem carteira assinada. Posso processar a empresa?",
      resposta: "Sim. Você pode ingressar com uma ação trabalhista para reconhecimento do vínculo empregatício e receber todas as verbas que teria direito se estivesse registrado, incluindo FGTS, férias, 13º e demais benefícios."
    },
    {
      pergunta: "Como funciona o adicional de insalubridade?",
      resposta: "O adicional de insalubridade é devido quando o trabalhador é exposto a agentes nocivos à saúde acima dos limites de tolerância. O valor varia entre 10%, 20% ou 40% do salário mínimo, conforme o grau de insalubridade (mínimo, médio ou máximo)."
    },
    {
      pergunta: "A empresa pode descontar o vale-transporte do meu salário?",
      resposta: "Sim, mas o desconto é limitado a 6% do salário básico do empregado. O restante do custo do transporte deve ser arcado pelo empregador. Se a empresa desconta mais que isso, está agindo de forma irregular."
    },
    {
      pergunta: "O que é rescisão indireta?",
      resposta: "A rescisão indireta é quando o empregado 'demite' o empregador por descumprimento grave das obrigações contratuais, como atraso reiterado de salários, assédio moral, ou não recolhimento do FGTS. Nesse caso, o trabalhador tem direito às mesmas verbas de uma demissão sem justa causa."
    },
  ];

  const servicosTrabalhadores = [
    { icon: <FileText className="w-5 h-5" />, title: "Verbas Rescisórias", desc: "Cálculo e cobrança de FGTS, multa, férias, 13º e aviso prévio" },
    { icon: <Clock className="w-5 h-5" />, title: "Horas Extras", desc: "Reconhecimento e pagamento de horas além da jornada" },
    { icon: <Shield className="w-5 h-5" />, title: "Adicionais", desc: "Insalubridade, periculosidade e trabalho noturno" },
    { icon: <Gavel className="w-5 h-5" />, title: "Rescisão Indireta", desc: "Quando o empregador descumpre suas obrigações" },
    { icon: <Users className="w-5 h-5" />, title: "Vínculo Empregatício", desc: "Reconhecimento de relação de trabalho" },
    { icon: <AlertTriangle className="w-5 h-5" />, title: "Assédio Moral", desc: "Proteção contra práticas abusivas" },
  ];

  const servicosEmpresas = [
    { icon: <Scale className="w-5 h-5" />, title: "Defesa Trabalhista", desc: "Representação estratégica em reclamações" },
    { icon: <Truck className="w-5 h-5" />, title: "Setor de Transporte", desc: "Experiência com empresas de logística" },
    { icon: <Shield className="w-5 h-5" />, title: "Setor de Vigilância", desc: "Atuação com empresas de segurança" },
    { icon: <Landmark className="w-5 h-5" />, title: "Recuperação Judicial", desc: "Créditos trabalhistas em processos recuperacionais" },
    { icon: <FileCheck className="w-5 h-5" />, title: "Auditoria", desc: "Revisão de práticas e conformidade" },
    { icon: <Briefcase className="w-5 h-5" />, title: "Consultoria Preventiva", desc: "Redução de passivos e prevenção de litígios" },
  ];

  return (
    <>
      <Helmet>
        <title>Advogado Trabalhista | Helio Lopes Leal Advocacia</title>
        <meta name="description" content="Advogado trabalhista especializado. Defesa de trabalhadores e empresas em reclamações trabalhistas, recursos, audiências e consultoria preventiva." />
        <meta name="keywords" content="advogado trabalhista, defesa trabalhista, reclamação trabalhista, direito do trabalho" />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-blue-900 font-medium tracking-wider text-sm mb-4">DIREITO DO TRABALHO</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Advogado <span className="text-blue-900">Trabalhista</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mb-8">
              Atuação estratégica na defesa de trabalhadores e empresas em reclamações trabalhistas, 
              elaboração de recursos, audiências, consultoria preventiva e gestão de passivo trabalhista.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-8 py-4 rounded font-medium transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Fale com Especialista
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center border-2 border-gray-300 hover:border-blue-900 text-gray-700 hover:text-blue-900 px-8 py-4 rounded font-medium transition-all"
              >
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>

        {/* Quando procurar */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quando Procurar um Advogado Trabalhista?
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Se você foi demitido sem receber todos os seus direitos, trabalhou sem registro, 
                não recebeu horas extras, sofreu assédio moral ou sua empresa está enfrentando 
                uma reclamação trabalhista, é hora de buscar orientação jurídica especializada.
              </p>
            </div>
          </div>
        </section>

        {/* Para Trabalhadores */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <Users className="w-8 h-8 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Defesa de Trabalhadores
              </h2>
            </div>
            <p className="text-gray-600 mb-10 max-w-2xl">
              Você teve seus direitos trabalhistas desrespeitados? Conte com assessoria jurídica 
              especializada para garantir o que é seu por direito.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicosTrabalhadores.map((servico, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-700 mb-4">
                    {servico.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{servico.title}</h4>
                  <p className="text-gray-600 text-sm">{servico.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Para Empresas */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Building2 className="w-8 h-8 text-blue-900" />
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Defesa de Empresas
              </h2>
            </div>
            <p className="text-gray-600 mb-10 max-w-2xl">
              Sua empresa está enfrentando uma reclamação trabalhista ou precisa de consultoria preventiva? 
              Oferecemos defesa estratégica e gestão de passivo trabalhista.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicosEmpresas.map((servico, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-100 hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-900 mb-4">
                    {servico.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{servico.title}</h4>
                  <p className="text-gray-600 text-sm">{servico.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferencial */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Por Que Nos Escolher em Questões Trabalhistas?
                </h2>
                <div className="space-y-4">
                  {[
                    "Experiência consolidada em audiências trabalhistas",
                    "Atuação em empresas de transporte e vigilância",
                    "Experiência em recuperação judicial",
                    "Elaboração de recursos estratégicos",
                    "Gestão completa de passivo trabalhista",
                    "Consultoria preventiva para empresas",
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-amber-50 p-8 rounded-xl border border-amber-200">
                <AlertTriangle className="w-10 h-10 text-amber-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Atenção aos Prazos</h3>
                <p className="text-gray-600 mb-6">
                  O prazo para ajuizar uma ação trabalhista é de <strong>2 anos</strong> após 
                  o término do contrato, podendo cobrar os últimos <strong>5 anos</strong> trabalhados.
                </p>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded font-medium transition-all w-full"
                >
                  <MessageCircle className="w-5 h-5" />
                  Não perca seu prazo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <HelpCircle className="w-10 h-10 text-blue-900 mx-auto mb-4" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Perguntas Frequentes
              </h2>
              <p className="text-gray-600">
                Tire suas principais dúvidas sobre Direito do Trabalho
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.pergunta}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.resposta}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-blue-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Precisa de um Advogado Trabalhista?
            </h2>
            <p className="text-blue-200 mb-8 max-w-xl mx-auto">
              Entre em contato e defenda seus direitos com quem entende do assunto.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Falar pelo WhatsApp
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded font-medium transition-all"
              >
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TrabalhistaPage;
